import { Component } from '@theme/component';
import { onAnimationEnd, prefersReducedMotion } from '@theme/utilities';

const ITEM_ANIMATION_CLASS = 'tabbed-product-list__animated-item';

/**
 * @typedef {Object} TabbedProductListRefs
 * @property {HTMLElement[]} panels
 */

/** @extends {Component<TabbedProductListRefs>} */
class TabbedProductListComponent extends Component {
    requiredRefs = ['panels'];

    #isAnimating = false;

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeydown);
        this.#updateViewAllLink(this.#activeIndex);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this.removeEventListener('click', this.#handleClick);
        this.removeEventListener('keydown', this.#handleKeydown);
    }

    get #activeIndex() {
        return Math.max(
            0,
            this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
        );
    }

    get #tabs() {
        const headerTabs = Array.from(
            this.querySelectorAll('.tabbed-product-list__tabs--header [data-tabbed-product-list-tab]')
        );

        if (headerTabs.length > 0) return headerTabs;

        return Array.from(this.querySelectorAll('.tabbed-product-list__tabs--fallback [data-tabbed-product-list-tab]'));
    }

    /**
     * @param {MouseEvent} event
     */
    #handleClick = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const tab = target.closest('[data-tabbed-product-list-tab]');
        if (!(tab instanceof HTMLElement) || !this.contains(tab)) return;

        const index = this.#tabs.indexOf(tab);
        if (index === -1) return;

        this.#activate(index);
    };

    /**
     * @param {KeyboardEvent} event
     */
    #handleKeydown = (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement) || target.getAttribute('role') !== 'tab') return;

        const tabs = this.#tabs;
        const currentIndex = tabs.indexOf(target);
        if (currentIndex === -1) return;

        const lastIndex = tabs.length - 1;
        const keyMap = {
            ArrowLeft: currentIndex - 1,
            ArrowRight: currentIndex + 1,
            Home: 0,
            End: lastIndex,
        };

        if (!(event.key in keyMap)) return;

        event.preventDefault();

        const nextIndex = (keyMap[event.key] + tabs.length) % tabs.length;
        this.#activate(nextIndex);
        tabs[nextIndex]?.focus();
    };

    /**
     * @param {number} selectedIndex
     */
    async #activate(selectedIndex) {
        const activeIndex = this.#activeIndex;
        if (this.#isAnimating || selectedIndex === activeIndex) return;

        const activePanel = this.refs.panels[activeIndex];
        const selectedPanel = this.refs.panels[selectedIndex];
        if (!activePanel || !selectedPanel) return;

        this.#updateTabs(selectedIndex);
        this.#updateViewAllLink(selectedIndex);

        if (prefersReducedMotion() || this.#getPanelItems(activePanel).length === 0) {
            this.#updatePanels(selectedIndex);
            requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
            return;
        }

        this.#isAnimating = true;

        try {
            await this.#animatePanel(activePanel, 'leaving');
            this.#setPanelVisibility(activePanel, false);
            this.#setPanelVisibility(selectedPanel, true);

            requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
            await this.#animatePanel(selectedPanel, 'entering');
        } finally {
            this.#isAnimating = false;
            requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
        }
    }

    /**
     * @param {number} selectedIndex
     */
    #updateTabs(selectedIndex) {
        for (const [index, tab] of this.#tabs.entries()) {
            const isActive = index === selectedIndex;

            tab.setAttribute('aria-selected', String(isActive));
            tab.setAttribute('tabindex', isActive ? '0' : '-1');
        }
    }

    /**
     * @param {number} selectedIndex
     */
    #updatePanels(selectedIndex) {
        for (const [index, panel] of this.refs.panels.entries()) {
            this.#setPanelVisibility(panel, index === selectedIndex);
        }
    }

    /**
     * @param {HTMLElement} panel
     * @param {boolean} isVisible
     */
    #setPanelVisibility(panel, isVisible) {
        panel.hidden = !isVisible;
        panel.toggleAttribute('inert', !isVisible);
        panel.setAttribute('tabindex', isVisible ? '0' : '-1');
    }

    /**
     * @param {HTMLElement} panel
     * @param {'entering' | 'leaving'} state
     */
    async #animatePanel(panel, state) {
        const items = this.#getPanelItems(panel);
        if (items.length === 0) return;

        for (const [index, item] of items.entries()) {
            item.classList.add(ITEM_ANIMATION_CLASS);
            item.style.setProperty('--tabbed-product-list-animation-index', String(index));
        }

        try {
            panel.dataset.tabbedProductListAnimation = state;
            await new Promise((resolve) => requestAnimationFrame(resolve));
            await onAnimationEnd(items, undefined, { subtree: false });
        } finally {
            panel.removeAttribute('data-tabbed-product-list-animation');

            for (const item of items) {
                item.classList.remove(ITEM_ANIMATION_CLASS);
                item.style.removeProperty('--tabbed-product-list-animation-index');
            }
        }
    }

    /**
     * @param {HTMLElement} panel
     * @returns {HTMLElement[]}
     */
    #getPanelItems(panel) {
        const selectors = [
            '.resource-list__slide',
            '.bento-box__item',
            '.resource-list__item',
            '.editorial-product__grid > :not(.editorial-product__spacer)',
        ];

        for (const selector of selectors) {
            const items = Array.from(panel.querySelectorAll(selector)).filter(
                (item) => item instanceof HTMLElement
            );

            if (items.length > 0) return /** @type {HTMLElement[]} */ (items);
        }

        return [];
    }

    /**
     * @param {number} selectedIndex
     */
    #updateViewAllLink(selectedIndex) {
        const header = this.querySelector('.section-resource-list__header');
        if (!(header instanceof HTMLElement)) return;

        const selectedTab = this.#tabs[selectedIndex];
        const url = selectedTab?.dataset.viewAllUrl;
        const viewAllLink = header.querySelector('a[href]');

        if (url && viewAllLink instanceof HTMLAnchorElement) viewAllLink.href = url;
    }
}

if (!customElements.get('tabbed-product-list-component')) {
    customElements.define('tabbed-product-list-component', TabbedProductListComponent);
}
